import {
  useCallback,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { updateChallengeById } from "../../challenges/services/challengeAdminService";
import type {
  ChallengeCategoryCode,
  ChallengeCategoryGender,
  ChallengeDetail,
} from "../../challenges/types/challengeAdmin";
import type {
  DraftPair,
  DraftPlayer,
} from "../../challenges/types/challengeDraft";
import { getPocoPairsCount } from "../../challenges/utils/pocoSchedule";
import type { TournamentCategoryModalState } from "../../tournaments/types/tournamentAdmin";
import type { TournamentCategoryEditorField } from "../components/TournamentCategoriesEditor";
import { normalizeChallengeDetail } from "../utils/adminChallengeEditor";
import {
  DEFAULT_TOURNAMENT_SHIRT_SIZES,
  buildUniqueTournamentCategoryKey,
  buildTournamentCategoriesPayload,
  normalizeChallengeRegistrationCategoriesForEdit,
  normalizeTournamentRegistrationCategoriesForEdit,
  normalizeTournamentShirtSizes,
  type TournamentRegistrationCategoryItem,
} from "../utils/tournamentCategories";

type UseAdminTournamentCategoryModalInput = {
  challengeDetail: ChallengeDetail | null;
  challengeTournamentCategories: TournamentRegistrationCategoryItem[];
  savingChallenge: boolean;
  setSavingChallenge: Dispatch<SetStateAction<boolean>>;
  loadHistory: () => Promise<void>;
  onCategorySaved?: () => void;
  selectTournamentCategoryForRegistrations: (categoryKey: string) => void;
  setActiveTournamentCategoryKey: Dispatch<SetStateAction<string>>;
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setChallengeRoundsJson?: Dispatch<SetStateAction<string>>;
  setDraftPairs: Dispatch<SetStateAction<DraftPair[]>>;
  setDraftPlayers: Dispatch<SetStateAction<DraftPlayer[]>>;
  setDrawCpf: Dispatch<SetStateAction<string>>;
  setPairCpf1: Dispatch<SetStateAction<string>>;
  setPairCpf2: Dispatch<SetStateAction<string>>;
  setTournamentCategoryModal: Dispatch<
    SetStateAction<TournamentCategoryModalState | null>
  >;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  tournamentCategoryModal: TournamentCategoryModalState | null;
  tournamentDraftCategoryKeyRef: MutableRefObject<string>;
  token?: string | null;
};

export function useAdminTournamentCategoryModal({
  challengeDetail,
  challengeTournamentCategories,
  loadHistory,
  onCategorySaved,
  savingChallenge,
  selectTournamentCategoryForRegistrations,
  setActiveTournamentCategoryKey,
  setChallengeDetail,
  setChallengeRoundsJson,
  setDraftPairs,
  setDraftPlayers,
  setDrawCpf,
  setPairCpf1,
  setPairCpf2,
  setSavingChallenge,
  setTournamentCategoryModal,
  showErrorToast,
  showSuccessToast,
  tournamentCategoryModal,
  tournamentDraftCategoryKeyRef,
  token,
}: UseAdminTournamentCategoryModalInput) {
  const t = siteTexts.admin;
  const normalizedChallengeType = String(challengeDetail?.challengeType || "")
    .trim()
    .toLowerCase();
  const isChallengeCategoryFlow =
    normalizedChallengeType === "challenge" || normalizedChallengeType === "poco";
  const isPocoCategoryFlow = normalizedChallengeType === "poco";
  const pocoPairLimit = getPocoPairsCount(challengeDetail?.courtsCount);
  const getChallengeCategorySlotLimit = useCallback(
    (registrationType: "individual" | "fixed_pair") =>
      isPocoCategoryFlow
        ? pocoPairLimit
        : registrationType === "fixed_pair"
          ? 6
          : 12,
    [isPocoCategoryFlow, pocoPairLimit],
  );

  const addTournamentCategoryOnChallenge = useCallback(() => {
    const nextKey =
      buildUniqueTournamentCategoryKey(challengeTournamentCategories);
    const registrationType = isPocoCategoryFlow
      ? "fixed_pair"
      : isChallengeCategoryFlow
        ? "individual"
        : "fixed_pair";
    setTournamentCategoryModal({
      mode: "create",
      originalCategoryKey: null,
      draft: {
        categoryKey: nextKey,
        courtsCount: isPocoCategoryFlow ? pocoPairLimit / 2 : null,
        categoryName: `Categoria ${challengeTournamentCategories.length + 1}`,
        categoryMale: "6a",
        categoryFemale: "6a",
        categoryGender: "mixed",
        registrationType,
        address: null,
        scheduledAt: null,
        scheduledEndAt: null,
        shirtSizesEnabled: false,
        shirtSizes: [],
        slotLimit: isChallengeCategoryFlow
          ? getChallengeCategorySlotLimit(registrationType)
          : 8,
        activeRegistrations: 0,
        confirmedRegistrations: 0,
        remainingSlots: isChallengeCategoryFlow
          ? getChallengeCategorySlotLimit(registrationType)
          : 8,
      },
    });
  }, [
    challengeTournamentCategories,
    getChallengeCategorySlotLimit,
    pocoPairLimit,
    isChallengeCategoryFlow,
    isPocoCategoryFlow,
    setTournamentCategoryModal,
  ]);

  const editTournamentCategoryOnChallenge = useCallback(
    (categoryKey: string) => {
      const category =
        challengeTournamentCategories.find(
          (item) => item.categoryKey === categoryKey,
        ) || null;

      if (!category) {
        console.warn("Categoria não encontrada para edição:", categoryKey);
        return;
      }

      setTournamentCategoryModal({
        mode: "edit",
        originalCategoryKey: category.categoryKey,
        draft: { ...category },
      });
    },
    [challengeTournamentCategories, setTournamentCategoryModal],
  );

  const updateTournamentCategoryModalDraft = useCallback(
    (field: TournamentCategoryEditorField, value: string) => {
      setTournamentCategoryModal((prev) => {
        if (!prev) return prev;
        if (field === "courtsCount" && isPocoCategoryFlow) {
          const courtsCount = Number(value) === 4 ? 4 : 3;
          return { ...prev, draft: { ...prev.draft, courtsCount, slotLimit: courtsCount * 2 } };
        }
        if (field === "slotLimit") {
          if (isChallengeCategoryFlow) return prev;
          const parsed = Math.floor(Number(value || 0));
          return {
            ...prev,
            draft: {
              ...prev.draft,
              slotLimit: Number.isFinite(parsed) && parsed > 0 ? parsed : 1,
            },
          };
        }
        if (field === "categoryMale" || field === "categoryFemale") {
          return {
            ...prev,
            draft: {
              ...prev.draft,
              [field]: value as ChallengeCategoryCode,
            },
          };
        }
        if (field === "categoryGender") {
          return {
            ...prev,
            draft: {
              ...prev.draft,
              categoryGender: value as ChallengeCategoryGender,
            },
          };
        }
        if (field === "registrationType") {
          const registrationType =
            !isPocoCategoryFlow && value === "individual"
              ? "individual"
              : "fixed_pair";
          return {
            ...prev,
            draft: {
              ...prev.draft,
              registrationType,
              ...(isChallengeCategoryFlow
                ? { slotLimit: isPocoCategoryFlow ? getPocoPairsCount(prev.draft.courtsCount ?? pocoPairLimit / 2) : getChallengeCategorySlotLimit(registrationType) }
                : {}),
            },
          };
        }
        if (field === "address") {
          return {
            ...prev,
            draft: {
              ...prev.draft,
              address: String(value || "").trim() || null,
            },
          };
        }
        if (field === "scheduledAt" || field === "scheduledEndAt") {
          return {
            ...prev,
            draft: {
              ...prev.draft,
              [field]: String(value || "").trim() || null,
            },
          };
        }
        if (field === "shirtSizes") {
          return {
            ...prev,
            draft: {
              ...prev.draft,
              shirtSizes: normalizeTournamentShirtSizes(value),
            },
          };
        }
        if (field === "shirtSizesEnabled") {
          const enabled = value === "true";
          return {
            ...prev,
            draft: {
              ...prev.draft,
              shirtSizesEnabled: enabled,
              ...(enabled
                ? normalizeTournamentShirtSizes(prev.draft.shirtSizes)
                    .length === 0
                  ? { shirtSizes: DEFAULT_TOURNAMENT_SHIRT_SIZES }
                  : {}
                : { shirtSizes: [] }),
            },
          };
        }
        return {
          ...prev,
          draft: {
            ...prev.draft,
            categoryName: value,
          },
        };
      });
    },
    [
      getChallengeCategorySlotLimit,
      pocoPairLimit,
      isChallengeCategoryFlow,
      isPocoCategoryFlow,
      setTournamentCategoryModal,
    ],
  );

  const persistTournamentCategories = useCallback(
    async (
      nextCategories: TournamentRegistrationCategoryItem[],
      activeCategoryKey: string,
      successMessage: string,
    ) => {
      if (!challengeDetail) return false;
      if (!token) {
        showErrorToast(t.createChallengePage.toasts.categorySaveError);
        return false;
      }
      setSavingChallenge(true);
      try {
        const updated = await updateChallengeById(token, challengeDetail.id, {
          tournamentCategories: buildTournamentCategoriesPayload(nextCategories),
        });
        setChallengeDetail(normalizeChallengeDetail(updated));
        if (isPocoCategoryFlow && nextCategories.some(category => {
          const old = challengeTournamentCategories.find(item => item.categoryKey === category.categoryKey);
          return old && old.courtsCount !== category.courtsCount;
        })) setChallengeRoundsJson?.(JSON.stringify(updated.rounds || [], null, 2));
        selectTournamentCategoryForRegistrations(activeCategoryKey);
        showSuccessToast(successMessage);
        await loadHistory();
        return true;
      } catch (err) {
        showErrorToast(
          err instanceof Error && err.message
            ? err.message
            : t.createChallengePage.toasts.categorySaveError,
        );
        return false;
      } finally {
        setSavingChallenge(false);
      }
    },
    [
      challengeDetail,
      loadHistory,
      isPocoCategoryFlow,
      challengeTournamentCategories,
      setChallengeRoundsJson,
      selectTournamentCategoryForRegistrations,
      setChallengeDetail,
      setSavingChallenge,
      showErrorToast,
      showSuccessToast,
      t.createChallengePage.toasts.categorySaveError,
      token,
    ],
  );

  const saveTournamentCategoryModal = useCallback(async () => {
    if (!tournamentCategoryModal || savingChallenge || !challengeDetail) return;
    const registrationType =
      !isPocoCategoryFlow &&
      tournamentCategoryModal.draft.registrationType === "individual"
        ? "individual"
        : "fixed_pair";
    const draft: TournamentRegistrationCategoryItem = {
      ...tournamentCategoryModal.draft,
      registrationType,
      categoryName:
        String(tournamentCategoryModal.draft.categoryName || "").trim() ||
        t.tournamentCategoryEditor.fallbackCategory,
      slotLimit: isChallengeCategoryFlow
        ? isPocoCategoryFlow ? getPocoPairsCount(tournamentCategoryModal.draft.courtsCount ?? pocoPairLimit / 2) : getChallengeCategorySlotLimit(registrationType)
        : Math.max(
            1,
            Math.floor(Number(tournamentCategoryModal.draft.slotLimit || 1)),
          ),
      address:
        String(tournamentCategoryModal.draft.address || "").trim() || null,
      scheduledAt:
        String(tournamentCategoryModal.draft.scheduledAt || "").trim() || null,
      scheduledEndAt: isChallengeCategoryFlow
        ? null
        : String(tournamentCategoryModal.draft.scheduledEndAt || "").trim() ||
          null,
      shirtSizesEnabled: Boolean(
        tournamentCategoryModal.draft.shirtSizesEnabled,
      ),
      shirtSizes: normalizeTournamentShirtSizes(
        tournamentCategoryModal.draft.shirtSizes,
      ),
    };
    if (!draft.shirtSizesEnabled) {
      draft.shirtSizes = [];
    }
    const currentCategories = isChallengeCategoryFlow
      ? normalizeChallengeRegistrationCategoriesForEdit(challengeDetail)
      : normalizeTournamentRegistrationCategoriesForEdit(
          challengeDetail.registrationCategories,
        );
    if (isChallengeCategoryFlow) {
      if (!String(draft.scheduledAt || "").trim()) {
        showErrorToast(t.createChallengePage.toasts.categoryTimeRequired);
        return;
      }
      const duplicate = currentCategories.some((item) => {
        if (
          tournamentCategoryModal.mode === "edit" &&
          item.categoryKey === tournamentCategoryModal.originalCategoryKey
        ) {
          return false;
        }
        return (
          String(item.categoryGender || "") === String(draft.categoryGender || "") &&
          String(item.categoryMale || "") === String(draft.categoryMale || "") &&
          String(item.categoryFemale || "") === String(draft.categoryFemale || "")
        );
      });
      if (duplicate) {
        showErrorToast(t.createChallengePage.toasts.duplicateChallengeCategory);
        return;
      }
    }
    const nextCategories =
      tournamentCategoryModal.mode === "edit"
        ? currentCategories.map((item) =>
            item.categoryKey === tournamentCategoryModal.originalCategoryKey
              ? draft
              : item,
          )
        : [...currentCategories, draft];
    const saved = await persistTournamentCategories(
      nextCategories,
      draft.categoryKey,
      t.createChallengePage.toasts.categorySaved,
    );
    if (saved) {
      onCategorySaved?.();
      setTournamentCategoryModal(null);
    }
  }, [
    challengeDetail,
    getChallengeCategorySlotLimit,
    pocoPairLimit,
    isChallengeCategoryFlow,
    isPocoCategoryFlow,
    onCategorySaved,
    persistTournamentCategories,
    savingChallenge,
    setTournamentCategoryModal,
    showErrorToast,
    t.createChallengePage.toasts.categorySaved,
    t.createChallengePage.toasts.categoryTimeRequired,
    t.createChallengePage.toasts.duplicateChallengeCategory,
    t.tournamentCategoryEditor.fallbackCategory,
    tournamentCategoryModal,
  ]);

  const removeTournamentCategoryOnChallenge = useCallback(
    (categoryKey: string) => {
      setChallengeDetail((prev) => {
        if (!prev) return prev;
        const currentCategories = isChallengeCategoryFlow
          ? normalizeChallengeRegistrationCategoriesForEdit(prev)
          : normalizeTournamentRegistrationCategoriesForEdit(
              prev.registrationCategories,
            );
        if (currentCategories.length <= 1) return prev;
        return {
          ...prev,
          registrationCategories: currentCategories.filter(
            (item) => item.categoryKey !== categoryKey,
          ),
        };
      });
      setActiveTournamentCategoryKey((prev) => {
        if (!prev || prev !== categoryKey) return prev;
        const fallback = challengeTournamentCategories.find(
          (item) => item.categoryKey !== categoryKey,
        );
        const fallbackKey = String(fallback?.categoryKey || "");
        tournamentDraftCategoryKeyRef.current = fallbackKey;
        setPairCpf1("");
        setPairCpf2("");
        setDrawCpf("");
        setDraftPairs([]);
        setDraftPlayers([]);
        setChallengeDetail((prevDetail) =>
          prevDetail ? { ...prevDetail, participantCpfs: [] } : prevDetail,
        );
        return fallbackKey;
      });
    },
    [
      challengeTournamentCategories,
      isChallengeCategoryFlow,
      setActiveTournamentCategoryKey,
      setChallengeDetail,
      setDraftPairs,
      setDraftPlayers,
      setDrawCpf,
      setPairCpf1,
      setPairCpf2,
      tournamentDraftCategoryKeyRef,
    ],
  );

  return {
    addTournamentCategoryOnChallenge,
    editTournamentCategoryOnChallenge,
    removeTournamentCategoryOnChallenge,
    saveTournamentCategoryModal,
    updateTournamentCategoryModalDraft,
  };
}
